import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { DIMENSOES, NIVEIS, DEFS_PADRAO } from "@/lib/constants";
import { Plus, X, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Sparkles, GripVertical, Library, Search, Check, ListPlus } from "lucide-react";

export const emptyCrit = () => ({
  nome: "", descricao: "", grupo: "", dimensao: "conhecimento", peso: 1, ordem: 0, critico: false, definicoes: {}, referencia: "", indicadores: [],
});

// Normaliza critérios vindos do backend garantindo os campos novos
export const normalizeCrit = (c) => ({
  nome: c.nome || "",
  descricao: c.descricao || "",
  grupo: c.grupo || "",
  dimensao: c.dimensao || "conhecimento",
  peso: c.peso ?? 1,
  ordem: c.ordem ?? 0,
  critico: !!c.critico,
  definicoes: c.definicoes || {},
  referencia: c.referencia || "",
  indicadores: (c.indicadores || []).map((i) => ({ nome: i.nome || "", descricao: i.descricao || "" })),
});

// Prepara critérios para envio ao backend (ordem = índice atual)
export const serializeCrits = (crits) =>
  crits
    .filter((c) => c.nome.trim())
    .map((c, i) => ({
      nome: c.nome.trim(),
      descricao: c.descricao || "",
      grupo: (c.grupo || "").trim(),
      dimensao: c.dimensao,
      peso: Number(c.peso) || 1,
      ordem: i,
      critico: !!c.critico,
      definicoes: c.definicoes || {},
      referencia: (c.referencia || "").trim(),
      indicadores: (c.indicadores || []).filter((x) => (x.nome || "").trim()).map((x) => ({ nome: x.nome.trim(), descricao: x.descricao || "" })),
    }));

export function CriteriosBuilder({ criterios, setCriterios, banco = [], bancoCriterios = [] }) {
  const [expanded, setExpanded] = useState(null);
  const [bcOpen, setBcOpen] = useState(false);
  const [bcBusca, setBcBusca] = useState("");

  const setCrit = (i, patch) =>
    setCriterios(criterios.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const setDef = (i, nivel, val) =>
    setCrit(i, { definicoes: { ...criterios[i].definicoes, [nivel]: val } });
  const addInd = (i, nome = "", descricao = "") =>
    setCrit(i, { indicadores: [...(criterios[i].indicadores || []), { nome, descricao }] });
  const setInd = (i, j, patch) =>
    setCrit(i, { indicadores: (criterios[i].indicadores || []).map((x, k) => (k === j ? { ...x, ...patch } : x)) });
  const removeInd = (i, j) =>
    setCrit(i, { indicadores: (criterios[i].indicadores || []).filter((_, k) => k !== j) });
  const add = () => setCriterios([...criterios, emptyCrit()]);
  const removeAt = (i) => setCriterios(criterios.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= criterios.length) return;
    const next = [...criterios];
    [next[i], next[j]] = [next[j], next[i]];
    setCriterios(next);
  };

  const nomesAtuais = new Set(criterios.map((c) => (c.nome || "").trim().toLowerCase()).filter(Boolean));
  const addFromBank = (b) => {
    setCriterios([...criterios, {
      nome: b.nome || "", descricao: b.descricao || "", grupo: b.grupo || "",
      dimensao: b.dimensao || "conhecimento", peso: b.peso ?? 1, ordem: 0,
      definicoes: b.definicoes || {}, referencia: b.referencia || "",
      indicadores: (b.indicadores || []).map((x) => ({ nome: x.nome || "", descricao: x.descricao || "" })),
    }]);
  };
  const toggleFromBank = (b) => {
    const key = (b.nome || "").trim().toLowerCase();
    if (nomesAtuais.has(key)) setCriterios(criterios.filter((c) => (c.nome || "").trim().toLowerCase() !== key));
    else addFromBank(b);
  };
  const bcFiltrados = bancoCriterios.filter((b) =>
    b.nome.toLowerCase().includes(bcBusca.toLowerCase()) || (b.grupo || "").toLowerCase().includes(bcBusca.toLowerCase()));

  // Insere TODOS os critérios do banco (na ordem 1..N), sem duplicar os já selecionados
  // e descartando linhas vazias iniciais. Não define mínimo/máximo.
  const inserirTodos = () => {
    const existentes = new Set(criterios.map((c) => (c.nome || "").trim().toLowerCase()).filter(Boolean));
    const base = criterios.filter((c) => (c.nome || "").trim());
    const novos = bancoCriterios
      .filter((b) => !existentes.has((b.nome || "").trim().toLowerCase()))
      .map((b) => ({
        nome: b.nome || "", descricao: b.descricao || "", grupo: b.grupo || "",
        dimensao: b.dimensao || "conhecimento", peso: b.peso ?? 1, ordem: 0,
        definicoes: b.definicoes || {}, referencia: b.referencia || "",
        indicadores: (b.indicadores || []).map((x) => ({ nome: x.nome || "", descricao: x.descricao || "" })),
      }));
    setCriterios([...base, ...novos]);
  };

  return (
    <div className="pt-2">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
        <div className="min-w-0">
          <Label>Critérios de avaliação</Label>
          <p className="text-[11px] text-muted-foreground">{criterios.filter((c) => (c.nome || "").trim()).length} critério(s) · sem limite</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!!bancoCriterios.length && (
            <Button type="button" size="sm" variant="outline" data-testid="banco-criterios-btn" onClick={() => { setBcBusca(""); setBcOpen(true); }}>
              <Library className="h-3.5 w-3.5 mr-1" />Banco de critérios
            </Button>
          )}
          {!!bancoCriterios.length && (
            <Button type="button" size="sm" variant="default" data-testid="inserir-todos-btn" onClick={inserirTodos}>
              <ListPlus className="h-3.5 w-3.5 mr-1" />Inserir todos os critérios
            </Button>
          )}
          <Button type="button" size="sm" variant="secondary" data-testid="add-criterio-btn" onClick={add}>
            <Plus className="h-3.5 w-3.5 mr-1" />Critério
          </Button>
        </div>
      </div>

      <Dialog open={bcOpen} onOpenChange={setBcOpen}>
        <DialogContent className="bg-popover border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Banco de critérios</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Adiciona uma cópia do critério (nome, CHA, peso, descrição, definições, indicadores e referência). Você pode ajustá-lo depois sem alterar a origem.</p>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input data-testid="banco-criterios-busca" value={bcBusca} onChange={(e) => setBcBusca(e.target.value)} placeholder="Buscar critério por nome ou grupo..." className="pl-8" />
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {bcFiltrados.map((b, idx) => {
              const jaTem = nomesAtuais.has(b.nome.trim().toLowerCase());
              const D = DIMENSOES[b.dimensao];
              return (
                <button key={idx} type="button" data-testid={`banco-crit-${idx}`}
                  onClick={() => toggleFromBank(b)}
                  className={`w-full flex items-start justify-between gap-2 border rounded-md p-2.5 text-left transition-colors ${jaTem ? "border-emerald-500/50 bg-emerald-500/10" : "border-border hover:bg-secondary/50"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[13px] leading-snug whitespace-normal break-words">{b.nome}</div>
                    <div className="text-[11px] text-muted-foreground break-words">{b.grupo ? `${b.grupo} · ` : ""}peso {b.peso ?? 1}{b.origem ? ` · ${b.origem}` : ""}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {D && <Badge className={`${D.badge} text-[10px]`}>{D.label}</Badge>}
                    {jaTem
                      ? <span className="text-[10px] text-emerald-400 flex items-center gap-1"><Check className="h-3.5 w-3.5" />Selecionado</span>
                      : <span className="text-[10px] text-primary flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Adicionar</span>}
                  </div>
                </button>
              );
            })}
            {!bcFiltrados.length && <p className="text-sm text-muted-foreground py-4 text-center">Nenhum critério no banco.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-2">
        {criterios.map((c, i) => {
          const isOpen = expanded === i;
          const D = DIMENSOES[c.dimensao];
          return (
            <div key={i} data-testid={`crit-row-${i}`} className="border border-border rounded-lg p-3 space-y-2 bg-card">
              <div className="flex items-center gap-2">
                <div className="flex flex-col text-muted-foreground">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="hover:text-foreground disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === criterios.length - 1} className="hover:text-foreground disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                <Input placeholder="Nome do critério" data-testid={`crit-nome-${i}`} value={c.nome} onChange={(e) => setCrit(i, { nome: e.target.value })} />
                <button type="button" data-testid={`crit-remove-${i}`} onClick={() => removeAt(i)} className="text-muted-foreground hover:text-red-400 shrink-0"><X className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input placeholder="Grupo / categoria" data-testid={`crit-grupo-${i}`} value={c.grupo} onChange={(e) => setCrit(i, { grupo: e.target.value })} />
                <Select value={c.dimensao} onValueChange={(v) => setCrit(i, { dimensao: v })}>
                  <SelectTrigger data-testid={`crit-dim-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {Object.entries(DIMENSOES).map(([k, d]) => <SelectItem key={k} value={k}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Label className="text-xs whitespace-nowrap">Peso</Label>
                  <Input type="number" min="0.5" step="0.5" data-testid={`crit-peso-${i}`} value={c.peso} onChange={(e) => setCrit(i, { peso: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className={D?.badge}>{D?.label}</Badge>
                  <div className="flex items-center gap-1.5">
                    <Switch data-testid={`crit-critico-${i}`} checked={!!c.critico} onCheckedChange={(v) => setCrit(i, { critico: v })} />
                    <span className="text-xs text-muted-foreground">Crítico</span>
                  </div>
                </div>
                <button type="button" data-testid={`crit-toggle-${i}`} onClick={() => setExpanded(isOpen ? null : i)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                  {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  Descrições / Indicadores
                </button>
              </div>
              {isOpen && (
                <div className="space-y-2 border-t border-border pt-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Descrição do critério</Label>
                    <Textarea data-testid={`crit-desc-${i}`} value={c.descricao} onChange={(e) => setCrit(i, { descricao: e.target.value })} placeholder="O que este critério avalia..." className="min-h-[52px]" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Referência doutrinária (opcional)</Label>
                    <Input data-testid={`crit-ref-${i}`} value={c.referencia || ""} onChange={(e) => setCrit(i, { referencia: e.target.value })} placeholder='Ex.: "POP 1.11"' className="h-8" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">Descrições por nível (1 a 5)</Label>
                    <Button type="button" size="sm" variant="ghost" data-testid={`crit-defs-padrao-${i}`} onClick={() => setCrit(i, { definicoes: { ...DEFS_PADRAO } })} className="h-7 text-xs">
                      <Sparkles className="h-3 w-3 mr-1" />Preencher padrão
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {NIVEIS.map((n) => (
                      <div key={n.value} className="flex items-start gap-2">
                        <div className="shrink-0 w-24 mt-1">
                          <Badge className={`${n.badge} w-full justify-center`}>{n.value} · {n.label}</Badge>
                          <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight break-words">{n.resumo}</p>
                        </div>
                        <Textarea data-testid={`crit-def-${i}-${n.value}`} value={c.definicoes?.[n.value] || ""} onChange={(e) => setDef(i, n.value, e.target.value)} className="min-h-[40px] text-sm" placeholder={n.resumo} />
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border pt-2">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                      <Label className="text-xs text-muted-foreground min-w-0">Indicadores observáveis (guia · "O que observar")</Label>
                      {!!banco.length && (
                        <Select value="" onValueChange={(v) => { const b = banco.find((x) => x.id === v); if (b) addInd(i, b.nome, b.descricao || ""); }}>
                          <SelectTrigger data-testid={`crit-ind-banco-${i}`} className="h-7 w-full sm:w-[190px] text-xs"><SelectValue placeholder="Adicionar do banco" /></SelectTrigger>
                          <SelectContent className="bg-popover border-border max-h-64">
                            {banco.map((b) => <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {(c.indicadores || []).map((ind, j) => (
                        <div key={j} data-testid={`crit-ind-${i}-${j}`} className="flex items-start gap-2">
                          <div className="flex-1 space-y-1">
                            <Input data-testid={`crit-ind-nome-${i}-${j}`} value={ind.nome} onChange={(e) => setInd(i, j, { nome: e.target.value })} placeholder="Indicador observável" className="h-8" />
                            <Textarea data-testid={`crit-ind-desc-${i}-${j}`} value={ind.descricao} onChange={(e) => setInd(i, j, { descricao: e.target.value })} placeholder="O que observar..." className="min-h-[34px] text-xs" />
                          </div>
                          <button type="button" data-testid={`crit-ind-remove-${i}-${j}`} onClick={() => removeInd(i, j)} className="text-muted-foreground hover:text-red-400 mt-1"><X className="h-4 w-4" /></button>
                        </div>
                      ))}
                    </div>
                    <Button type="button" size="sm" variant="ghost" data-testid={`crit-add-ind-${i}`} onClick={() => addInd(i)} className="h-7 text-xs mt-1.5">
                      <Plus className="h-3 w-3 mr-1" />Indicador
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!criterios.length && <p className="text-sm text-muted-foreground">Nenhum critério. Adicione o primeiro.</p>}
      </div>
    </div>
  );
}
